<template>
    <div>
        <div
            class="bg w-full md:h-screen relative overflow-hidden md:flex-col flex items-center justify-center py-24 md:py-0"
        >
            <div
                class="p-24 md:p-32 w-full max-w-[85%] sm:max-w-[400px] bg-white rounded-6 shadow-custom flex flex-col items-center z-10 self-start md:self-center">

                <h1 class="text-heading-1 leading-none text-black">
                    Jobtoolz
                </h1>
                <h2 class="text-heading-1 text-secondary mb-24 leading-none">
                    Login
                </h2>

                <div v-if="selectionScreen" class="space-y-12">
                    <simple-button
                        type="submit"
                        display="secondary"
                        look="primary"
                        class="w-full"
                        @click="goToSso"
                    >
                        {{ $t('auth.login_with') }} {{ saml2Provider }}
                    </simple-button>
                    <simple-button
                        type="submit"
                        look="secondary"
                        class="w-full"
                        @click="selectionScreen = false"
                    >
                        {{ $t('auth.other_options') }}
                    </simple-button>
                </div>

                <form
                    class="w-full space-y-16"
                    v-else
                    @keydown.enter.prevent="handleSubmit"
                    @keydown.esc="handleReset"
                    @submit.prevent="handleSubmit"
                >
                    <banner
                        danger
                        :description="$t('auth.reason.timeout')"
                        inline
                        v-if="$route.query.reason === 'timeout'"
                    />

                    <div class="space-y-8">
                        <input-component
                            autofocus
                            class="md:min-w-320 first-letter:uppercase w-full"
                            :input-label="$t('auth.your_email')"
                            name="email"
                            placeholder="john.doe@example.com"
                            required
                            type="email"
                            v-model="form.email"
                        />

                        <input-component
                            class="md:min-w-320 w-full"
                            :input-label="$t('auth.your_password')"
                            name="password"
                            required
                            type="password"
                            v-if="!ssoLogin"
                            v-model="form.password"
                        >
                            {{ $t('auth.your_password') }}
                        </input-component>

                        <div v-if="!ssoLogin" class="flex items-center">
                            <checkbox
                                id="remember"
                                v-model="form.remember"
                            >
                                {{ $t('auth.remember') }}
                            </checkbox>
                        </div>

                        <div v-if="hasErrors" class="mb-16 self-start">
                            <ul class="text-semantic-error text-body-sm">
                                <li v-for="(error, index) in allErrors" :key="index" class="flex items-center">
                                    <svg-icon type="warning" class="max-w-12 max-h-12 mr-4"></svg-icon>
                                    <span>{{ error }}</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div class="space-y-12">
                        <simple-button
                            type="submit"
                            display="secondary"
                            look="primary"
                            :loading="form.submitting"
                            class="w-full"
                        >
                            {{ $t('auth.login') }}
                        </simple-button>
                        <simple-button
                            v-if="!ssoLogin"
                            @click="toggleSsoLogin"
                            look="secondary"
                            class="w-full"
                        >
                            {{ $t('auth.sso.signin') }}
                        </simple-button>
                        <simple-button
                            v-else
                            @click="toggleSsoLogin"
                            look="secondary"
                            class="w-full"
                        >
                            {{ $t('auth.login_with_email') }}
                        </simple-button>
                    </div>

                   <div class="space-y-8">
                       <simple-button
                           class="w-full text-center"
                           display="neutral"
                           look="text"
                           @click="gotoReset"
                       >
                           {{ $t('auth.forgot_password') }}
                       </simple-button>

                       <simple-button
                           class="w-full text-center"
                           display="neutral"
                           look="text"
                           @click="gotoRegister"
                       >
                           {{ regionTrialRegister }}
                       </simple-button>
                   </div>

                </form>
            </div>
        </div>
    </div>
</template>

<script>
import Form from '../etc/forms/Form';
import values from 'lodash/values';
import flatten from 'lodash/flatten';
import Input from '../components/forms/Input.vue';
import SimpleButton from '../components/ui/SimpleButton.vue';
import SvgIcon from '../components/ui/SvgIcon.vue';
import {ADD_NOTIFICATION} from '../state/mutation-types';
import Checkbox from "../components/forms/Checkbox.vue";
import Banner from "../components/ui/Banner.vue";
import {mapActions, mapStores} from "pinia";
import {useNotificationsStore} from "../state/modules/notifications";
import {useAuthenticationStore} from "../state/modules/authentication";

export default {
    components: {
        Banner,
        Checkbox,
        'input-component': Input,
        'simple-button': SimpleButton,
        'svg-icon': SvgIcon,
    },
    head() {
        return {
            title: this.$t('auth.login'),
        }
    },
    data: function () {
        return {
            selectionScreen: false,
            ssoLogin: false,
            saml2Uuid: null,
            saml2Provider: null,
            redirectAfterLogin: null,

            form: new Form([
                { name: 'email', value: '', rules: ['required']},
                { name: 'password', value: '', rules: ['required']},
                { name: 'remember', value: false},
            ]),
        }
    },
    computed: {
        ...mapStores(useAuthenticationStore),
        hasErrors () {
            return values(this.form.errors).some(errors => errors !== undefined);
        },
        allErrors () {
            return flatten(values(this.form.errors).filter(error => error && error.length));
        },
        settingsHome () {
            if (this.can.settings.company) {
                return 'settings.company';
            } else if (this.can.settings.users) {
                return 'settings.users';
            } else if (this.can.settings.jobsite) {
                return 'settings.languages';
            } else if (this.can.settings.locations) {
                return 'settings.locations';
            } else if (this.can.settings.emailTemplates) {
                return 'settings.email-templates';
            } else if (this.can.settings.tags) {
                return 'settings.tags';
            } else if (this.can.settings.competences) {
                return 'settings.competences';
            } else {
                return 'settings.questions';
            }
        },
        homepage () {
            return {
                name: this.can.jobs ? 'jobs' : (this.can.applications ? 'applications' : (this.can.jobsite ? 'branding' : (this.can.settings.home ? 'settings.default' : 'user')))
            };
        },
        samlUrl() {
            if (this.saml2Uuid) {
                return window.location.protocol + '//' + window.location.host + "/saml2/" + this.saml2Uuid + "/login";
            }
        },
        can () {
            return {
                applications: this.$can([
                    'applications.view',
                    'applications.create',
                    'applications.update',
                    'applications.delete'
                ]),
                jobs: this.$can([
                    'jobs.create',
                    'jobs.update',
                    'jobs.delete',
                    'jobs.view',
                    'jobs.view.*',
                ]),
                jobsite: this.$can(['jobsites.create', 'jobsites.update', 'jobsites.delete']),
                settings : {
                    home: this.$can([
                        'email_templates.create',
                        'email_templates.update',
                        'email_templates.delete',
                        'company_items.create',
                        'company_items.update',
                        'company_items.delete',
                        'roles.create',
                        'roles.update',
                        'roles.delete',
                        'companies.update',
                        'jobsites.update',
                        'tags.create',
                        'tags.update',
                        'tags.delete',
                        'competences.update',
                        'competences.delete',
                        'questions.create',
                        'questions.update',
                        'questions.delete',
                    ]),
                    company: this.$can(['companies.update']),
                    jobsite: this.$can('jobsites.update'),
                    users: this.$can([
                        'roles.create',
                        'roles.update',
                        'roles.delete',
                    ]),
                    emailTemplates: this.$can([
                        'email_templates.create',
                        'email_templates.update',
                        'email_templates.delete',
                    ]),
                    locations: this.$can([
                        'company_items.create',
                        'company_items.update',
                        'company_items.delete',
                    ]),
                    tags: this.$can([
                        'tags.create',
                        'tags.update',
                        'tags.delete',
                    ]),
                    competences: this.$can([
                        'competences.update',
                        'competences.delete',
                    ]),
                    questions: this.$can([
                        'questions.create',
                        'questions.update',
                        'questions.delete',
                    ]),
                }
            }
        },
        regionTrialRegister() {
            if (Jobtoolz.region === 'AU') {
                return this.$t('auth.register_AU');
            } else {
                return this.$t('auth.register');
            }
        },
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        handleSubmit () {
            this.form.submit((data) => {
                return this.authenticationStore.login({
                    data,
                    ssoLogin: this.ssoLogin,
                    redirectAfterLogin: this.redirectAfterLogin
                }).then((ssoActivated) => {
                    if (!ssoActivated && this.ssoLogin) {
                        this[ADD_NOTIFICATION]({
                            type: 'error',
                            message: this.$t('auth.failed_company_not_sso'),
                        });
                    }
                });
            }).catch(error => {});
        },
        handleReset () {
            this.form.reset();
        },
        gotoReset () {
            this.$router.push({ name: 'forgot-password'});
        },
        gotoRegister () {
            this.$router.push({ name: 'register'});
        },
        toggleSsoLogin () {
            this.ssoLogin = !this.ssoLogin;

            this.form.resetErrors();
        },
        goToSso() {
            this.$router.push({name: 'saml2-login', params: {uuid: this.saml2Uuid}});
        }
    },
    mounted() {
        try {
            document.querySelector('.logo').classList.remove('hidden')
        } catch (e) {}

        this.saml2Uuid = localStorage.getItem('saml2Uuid');
        this.saml2Provider = localStorage.getItem('saml2Provider');

        if (this.$route.query.error) {
            this.form.errors = {general: this.$t(this.$route.query.error)};
        }

        if (this.$route.query.sso) {
            this.ssoLogin = true;
        }

        if (this.$route.query.email) {
            this.form.email = this.$route.query.email;
        }

        if (this.$route.query.path) {
            this.redirectAfterLogin = this.$route.query.path;
        }

        this.$router.replace({query: {}});

        if (this.samlUrl && !(this.ssoLogin || Object.values(this.form.errors).filter(error => error && error.length).length)) {
            this.selectionScreen = true;
        }

        // start alpine to configure the cookie popup
        if (window.Alpine) {
            window.Alpine.start();
        }
    }
}
</script>
